import { getLogger } from '../../diagnostics/logger';
import moment from 'moment';
import { Action, ActionMeta } from '../../actions';
import {
  LOAD_TARIFF_BOLT_ONS_FAILURE,
  LOAD_TARIFF_BOLT_ONS_PENDING,
  LOAD_TARIFF_BOLT_ONS_SUCCESS,
} from '../../actions/actionTypes';
import { BoltOnItem } from '../../components/ui/input/DropdownInput';
import { SimManagementError } from '../../models/Errors';
import { LoadBoltOnsPayload } from '../../models/payload/loadRequests';
import { initialAppState } from '../../models/state/appState';
import { BoltOnsStateCacheTimeout } from '../../models/state/simProvision/cacheTimeouts';
import { getDefaultAvailableBoltOns, getDefaultBoltOnState } from '../../models/state/simProvision/defaults';
import { BoltOnsState, BoltOnState, getAccountNetworkKey } from '../../models/state/simProvision/simProvisionState';
const log = getLogger('simManagement.api.boltOnReducer');

export default function boltOnReducer(
  state: BoltOnsState = initialAppState.simProvisionState.boltOns,
  action: Action<any>,
): BoltOnsState {
  switch (action.type) {
    case LOAD_TARIFF_BOLT_ONS_PENDING: {
      const typedAction = action as Action<LoadBoltOnsPayload>;
      const { payload } = typedAction;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      if (payload.isTemporal) {
        return state;
      }

      const changedNetworkState: Partial<BoltOnState> = {
        availableBoltOns: {
          ...getDefaultAvailableBoltOns(),
          isLoading: true,
        },
        selection: {
          ...payload,
        },
      };

      const { accountId, networkId } = payload;
      const networkAccountKey = getAccountNetworkKey(accountId, networkId);
      const oldNetworkState = state[networkAccountKey] || getDefaultBoltOnState({ accountId, networkId });
      const networkState = Object.assign({}, oldNetworkState, changedNetworkState);
      const newState = Object.assign({}, state, {
        [networkAccountKey]: networkState,
      });

      return newState;
    }
    case LOAD_TARIFF_BOLT_ONS_FAILURE: {
      const typedAction = action as ActionMeta<SimManagementError, LoadBoltOnsPayload>;
      const { meta, payload } = typedAction;
      if (!meta) {
        log.warn('No meta');
        return state;
      }

      if (meta.isTemporal) {
        return state;
      }

      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const changedNetworkState: Partial<BoltOnState> = {
        availableBoltOns: {
          ...getDefaultAvailableBoltOns(),
          error: payload,
        },
        selection: {
          ...meta,
        },
      };

      const networkAccountKey = getAccountNetworkKey(meta.accountId, meta.networkId);
      const oldNetworkState = state[networkAccountKey] || getDefaultBoltOnState(meta);
      const networkState = Object.assign({}, oldNetworkState, changedNetworkState);
      const newState = Object.assign({}, state, {
        [networkAccountKey]: networkState,
      });

      return newState;
    }
    case LOAD_TARIFF_BOLT_ONS_SUCCESS: {
      const typedAction = action as ActionMeta<BoltOnItem[], LoadBoltOnsPayload>;
      const { meta, payload } = typedAction;
      if (!meta) {
        log.warn('No meta');
        return state;
      }

      if (meta.isTemporal) {
        return state;
      }

      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const changedNetworkState: Partial<BoltOnState> = {
        availableBoltOns: {
          items: [...payload],
          byId: Object.assign({}, ...payload.map((a: any) => ({
            [a.key]: a
          }))),
          isLoading: false,
          expiresOn: moment().add(BoltOnsStateCacheTimeout, 'seconds').toDate(),
          lastLoadedOn: new Date(),
        },
        selection: {
          ...meta,
        },
      };

      const networkAccountKey = getAccountNetworkKey(meta.accountId, meta.networkId);
      const oldNetworkState = state[networkAccountKey] || getDefaultBoltOnState(meta);
      const networkState = Object.assign({}, oldNetworkState, changedNetworkState);
      const newState = Object.assign({}, state, {
        [networkAccountKey]: networkState,
      });

      return newState;
    }
    default:
      return state;
  }
}
